// Carries a `?org=` deep link across the Kinde round-trip; used once, only if the org is in the user's list (INV-11).
// Own key, not `od`: a cold boot without a session clears `od`.
const KEY = 'pendingOrgLink';
const TTL_MS = 20 * 60 * 1000;

/**
 * The org a URL links to, and the URL without it.
 * @param {string} href
 * @returns {{ orgID: number|null, cleaned: string|null }} cleaned is null when there was no `org` param.
 */
export const readOrgParam = (href) => {
	const url = new URL(href);
	if (!url.searchParams.has('org')) return { orgID: null, cleaned: null };
	const raw = url.searchParams.get('org');
	url.searchParams.delete('org');
	const orgID = /^[1-9]\d{0,9}$/.test(raw ?? '') ? Number(raw) : null;
	return { orgID, cleaned: url.pathname + url.search + url.hash };
};

/** Boot: strips `?org=` from the address bar (so nothing copies it forward) and stashes a valid one. */
export const stashOrgLink = () => {
	try {
		const { orgID, cleaned } = readOrgParam(window.location.href);
		if (cleaned === null) return;
		window.history.replaceState(window.history.state, '', cleaned);
		if (orgID) localStorage.setItem(KEY, JSON.stringify({ orgID, createdAt: Date.now() }));
	} catch {
		// storage unavailable or a malformed URL — the link just doesn't pick the org.
	}
};

/** Stashes an org chosen by an app-built link (the Billing return), for the same one-time pick as `?org=`. */
export const stashOrgID = (orgID) => {
	try {
		if (Number.isSafeInteger(orgID) && orgID > 0)
			localStorage.setItem(KEY, JSON.stringify({ orgID, createdAt: Date.now() }));
	} catch {
		// storage unavailable — the link just doesn't pick the org.
	}
};

/** The stashed org, if any and within TTL; cleared either way, so it's used at most once. */
export const takeOrgLink = () => {
	try {
		const raw = localStorage.getItem(KEY);
		localStorage.removeItem(KEY);
		if (!raw) return null;
		const { orgID, createdAt } = JSON.parse(raw);
		if (!Number.isSafeInteger(orgID) || orgID <= 0 || typeof createdAt !== 'number') return null;
		return Date.now() - createdAt > TTL_MS ? null : orgID;
	} catch {
		return null;
	}
};
